<template>
    <div>
        <v-container fluid>
            <!-- Breadcrumbs -->
            <div class="mb-2 mt-2">
                <v-breadcrumbs :items="breadcrumbs" class="pa-0">
                    <template v-slot:divider>
                        <v-icon>mdi-chevron-right</v-icon>
                    </template>
                </v-breadcrumbs>
            </div>

            <h1 class="font-weight-bolder my-2">{{ $t("farm.farmsAccessControl") }}</h1>

            <!-- Data Table Card -->
            <v-card elevation="0" class="px-5 py-1 mt-2">
                <v-card-text>
                    <div class="d-flex align-center mt-2">
                        <div class="d-flex flex-column align-start">
                            <h2 class="mb-2 font-weight-bold">
                                {{ mode === 'view' ? $t("farm.viewAndUpdateTheFarmsThisUserHasAccessTo") : $t("farm.assignFarmsToTheSelectedUsers") }}
                            </h2>
                            <p class="text--secondary mb-2">
                                {{ mode === 'view' ? $t("farm.addNewFarmsOrRemoveExistingOnesToControlThisUser'sAccess") : $t("farm.selectTheFarmsYouWantToGrantAccessToForTheChosenTechnicians") }}
                            </p>
                            <div v-if="selectedUsers.length > 0" class="mt-2">
                                <span class="font-weight-medium">{{ $t("farm.farmUser") }}{{ selectedUsers.length > 1 ? $t("farm.s") : '' }}: </span>
                                <span class="text--secondary">
                                    {{ selectedUsers.map(u => u.userName).join(', ') }}
                                </span>
                            </div>
                        </div>

                        <v-spacer></v-spacer>

                        <v-btn 
                            outlined 
                            class="mr-2" 
                            color="primary"
                            @click="$emit('back')"
                        >
                            <v-icon left>mdi-arrow-left</v-icon>
                            {{ $t("farm.back") }}
                        </v-btn>

                        <!-- Remove Access Button (shown in grant mode from handleProceed) -->
                        <v-btn 
                            v-if="mode === 'view'"
                            dark 
                            class="mr-2" 
                            color="primary"
                            :disabled="selectedFarms.length === 0"
                            @click="handleRemoveAccess"
                            style="text-transform: uppercase; font-weight: bold;"
                        >
                            {{ $t("farm.removeAccessFromSelectedFarms") }}
                        </v-btn>

                        <!-- Grant Farm Access Button -->
                        <v-btn 
                            v-if="mode === 'grant'"
                            dark 
                            class="mr-2" 
                            color="primary" 
                            :disabled="selectedFarms.length === 0"
                            @click="handleGrantAccess"
                            style="text-transform: uppercase; font-weight: bold;"
                        >
                            {{ $t("farm.grantFarmAccess") }}
                        </v-btn>
                    </div>

                    <v-row style="margin-left: -30px;">
                        <v-col cols="12">
                            <v-data-table 
                                :headers="tableHeaders" 
                                :items="farms" 
                                :items-per-page="options.limit"
                                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }" 
                                item-key="id"
                                hide-default-footer 
                                :loading="loading" 
                                :options.sync="options"
                                :server-items-length="totalFarms" 
                                :loading-text="$t('farm.loadingData')" 
                                show-select
                                v-model="selectedFarms" 
                                :single-select="false"
                            >
                                <template v-slot:top>
                                    <div class="d-flex mt-5 align-center flex-wrap">
                                        <!-- Search Field -->
                                        <div class="ml-3 control" style="width: 280px;">
                                            <label for="text">&nbsp;</label>
                                            <v-text-field 
                                                prepend-inner-icon="mdi-magnify" 
                                                outlined 
                                                placeholder="Search"
                                                dense 
                                                v-model="search" 
                                                @input="searchQuery"
                                                class="shrink"
                                            ></v-text-field>
                                        </div>

                                        <!-- Society Dropdown -->
                                        <div class="ml-3 control">
                                            <label>{{ $t("farm.factory") || $t("farm.selectFactory") }}</label>
                                            <v-autocomplete
                                            outlined
                                            dense
                                            v-model="selectedSociety"
                                            :items="societyList"
                                            item-value="id"
                                            item-text="name"
                                            :placeholder="$t('farm.factory')"
                                            return-object
                                            clearable
                                            @change="handleSocietyChange"
                                            v-on:click:clear="handleClearSociety"
                                            >
                                            </v-autocomplete>
                                        </div>

                                        <v-spacer></v-spacer>

                                        <!-- Pagination Info -->
                                        <div class="py-1 title">
                                            {{ currentPageItems }} {{ $t("farm.of") }} {{ totalFarms }}
                                        </div>

                                        <!-- Pagination Controls -->
                                        <v-btn 
                                            class="mx-2" 
                                            fab 
                                            small 
                                            outlined 
                                            color="primary"
                                            :disabled="options.page <= 1" 
                                            @click="pageChange(false)"
                                        >
                                            <v-icon dark>mdi-chevron-left</v-icon>
                                        </v-btn>
                                        <v-btn 
                                            class="mx-2" 
                                            fab 
                                            small 
                                            outlined 
                                            color="primary"
                                            :disabled="options.page >= totalPages" 
                                            @click="pageChange(true)"
                                        >
                                            <v-icon dark>mdi-chevron-right</v-icon>
                                        </v-btn>

                                        <!-- Customization Menu -->
                                        <v-menu 
                                            v-model="menu" 
                                            :close-on-content-click="false" 
                                            :nudge-width="200" 
                                            left
                                            :min-width="417"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn 
                                                    class="mx-2" 
                                                    fab 
                                                    small 
                                                    outlined 
                                                    color="primary" 
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    <v-icon dark>mdi-cog-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-card width="417">
                                                <v-list>
                                                    <v-list-item>
                                                        <v-list-item-title>{{ $t("farm.customization") }}</v-list-item-title>
                                                    </v-list-item>
                                                    <v-divider></v-divider>
                                                    <div class="pl-3 pt-2 font-weight-normal">
                                                        {{ $t("farm.recordsPerPage") }}
                                                    </div>
                                                    <v-list-item>
                                                        <v-slider 
                                                            v-model="itemsPerPageSlider"
                                                            :tick-labels="itemsPerPageOptions" 
                                                            :max="3" 
                                                            step="1"
                                                            ticks="always" 
                                                            tick-size="4"
                                                        ></v-slider>
                                                    </v-list-item>
                                                    <v-divider class="mt-n3"></v-divider>
                                                </v-list>
                                                <v-card-text>
                                                    <v-container fluid>
                                                        <v-row>
                                                            <v-col 
                                                                class="mt-n9" 
                                                                cols="6" 
                                                                v-for="header in headers"
                                                                :key="header.value"
                                                            >
                                                                <v-checkbox 
                                                                    v-if="header.text" 
                                                                    v-model="selectedHeaders"
                                                                    :label="header.text" 
                                                                    color="primary"
                                                                    :value="header.text" 
                                                                    hide-details
                                                                ></v-checkbox>
                                                            </v-col>
                                                        </v-row>
                                                    </v-container>
                                                </v-card-text>
                                                <v-layout justify-center>
                                                    <v-card-actions class="mb-5">
                                                        <v-spacer></v-spacer>
                                                        <v-btn 
                                                            outlined 
                                                            color="primary" 
                                                            @click="resetDefault"
                                                            width="190"
                                                        >
                                                            {{ $t("farm.restoreDefault") }}
                                                        </v-btn>
                                                        <v-btn 
                                                            color="primary" 
                                                            @click="resetTableStructure" 
                                                            width="190"
                                                        >
                                                            {{ $t("farm.apply") }}
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-layout>
                                            </v-card>
                                        </v-menu>

                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn 
                                                    class="mx-2" 
                                                    fab 
                                                    small 
                                                    outlined 
                                                    color="primary" 
                                                    v-bind="attrs"
                                                    v-on="on" 
                                                    @click="printPDF"
                                                >
                                                    <v-icon dark>mdi-printer</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t("farm.print") }}</span>
                                        </v-tooltip>
                                    </div>
                                </template>

                                <!-- Farm Name & ID Column -->
                                <template v-slot:item.farmNameAndId="{ item }">
                                    <div>
                                        <div class="font-weight-medium">{{ item.farmName }}</div>
                                        <div class="text-caption text--secondary">{{ item.farmId }}</div>
                                    </div>
                                </template>

                                <!-- Farmer Name Column -->
                                <template v-slot:item.farmerName="{ item }">
                                    <span>{{ item.farmerName || '-' }}</span>
                                </template>

                                <!-- Farm Address Column -->
                                <template v-slot:item.farmAddress="{ item }">
                                    <span class="text-truncate" style="max-width: 300px;">{{ item.farmAddress || '-' }}</span>
                                </template>

                                <!-- Reg Date Column -->
                                <template v-slot:item.regDate="{ item }">
                                    <span>{{ item.regDate || '-' }}</span>
                                </template>

                                <!-- Access Column -->
                                <template v-slot:item.access="{ item }">
                                    <a 
                                        href="#" 
                                        class="access-link" 
                                        @click.prevent="selectFarm(item)"
                                    >
                                        {{ $t("farm.selectFarm") }}
                                    </a>
                                </template>

                                <template v-slot:footer></template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-container>

        <!-- Confirmation Dialog for Grant Access -->
        <ConfirmationBox 
            :dialog="confirmGrantDialog" 
            actionType="accept" 
            :message="confirmGrant.message"
            :confirmData="confirmGrant.confirmData" 
            :dialogTitle="confirmGrant.title"
            :confirmButtonText="confirmGrant.confirmBtnText" 
            @close="confirmGrantDialog = false"
            @confirm="confirmGrantAccess" 
        />

        <!-- Confirmation Dialog for Remove Access -->
        <ConfirmationBox 
            :dialog="confirmRemoveDialog" 
            actionType="reject" 
            :message="confirmRemove.message"
            :confirmData="confirmRemove.confirmData" 
            :dialogTitle="confirmRemove.title"
            :confirmButtonText="confirmRemove.confirmBtnText" 
            @close="confirmRemoveDialog = false"
            @confirm="confirmRemoveAccess" 
        />
    </div>
</template>

<script>
import MemberDataService from "@/_services/MemberDataService";
import ConfirmationBox from "./ConfirmationBox.vue";
import FarmService from "@/_services/FarmService";
import BuyingStationService from "@/_services/BuyingStationService";
import loadingMixin from "@/mixins/LoadingMixin";
import download from "@/mixins/DownloadMixin";
import html2pdf from "html2pdf.js";
import printJS from "print-js";

export default {
    name: "GrantFarmAccess",
    components: {
        ConfirmationBox,
    },
    props: {
        selectedUsers: {
            type: Array,
            default: () => [],
        },
        mode: {
            type: String,
            default: 'grant', // 'view' or 'grant'
            validator: (value) => ['view', 'grant'].includes(value),
        },
    },
    async mounted() {
        // Initialize headers with translations
        this.headers = [
            {
                text: this.$t("farm.farmNameAndId"),
                align: "start",
                value: "farmNameAndId",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.farmerName"),
                align: "start",
                value: "farmerName",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.farmAddress"),
                align: "start",
                value: "farmAddress",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.regDate"),
                align: "start",
                value: "regDate",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.access"),
                align: "start",
                value: "access",
                class: "black--text",
                sortable: false,
            },
        ];
        
        // Initialize allSelectedHeaders with translations
        this.allSelectedHeaders = [
            this.$t("farm.farmNameAndId"),
            this.$t("farm.farmerName"),
            this.$t("farm.farmAddress"),
            this.$t("farm.regDate"),
            this.$t("farm.access"),
        ];
        
        // Initialize confirm dialogs with translations
        this.confirmGrant.title = this.$t("farm.grantFarmAccess");
        this.confirmGrant.confirmBtnText = this.$t("farm.grantAccess");
        this.confirmGrant.cancelBtnText = this.$t("farm.cancel");
        
        this.confirmRemove.title = this.$t("farm.removeFarmAccess");
        this.confirmRemove.confirmBtnText = this.$t("farm.removeAccess");
        this.confirmRemove.cancelBtnText = this.$t("farm.cancel");
        
        this.tableHeaders = this.headers;
        this.selectedHeaders = this.allSelectedHeaders;
        // Initialize slider to match current limit
        const currentLimitIndex = this.itemsPerPageOptions.indexOf(this.options.limit);
        this.itemsPerPageSlider = currentLimitIndex >= 0 ? currentLimitIndex : 1;
        
        // If in view mode, fetch farms that user has access to first
        if (this.mode === 'view' && this.selectedUsers.length > 0) {
            await this.loadUserAccessibleFarms();
        }
        
        // Load farm data (will filter if in view mode)
        await this.loadFarmData();
        this.getSocietyList();
    },
    computed: {
        totalPages() {
            return parseInt(Math.ceil(this.totalFarms / this.options.limit));
        },
        currentPageItems() {
            if (this.totalFarms === 0) {
                return '0';
            }
            const start = (this.options.page - 1) * this.options.limit + 1;
            const end = Math.min(this.options.page * this.options.limit, this.totalFarms);
            if (start === end) {
                return start.toString();
            }
            return `${start}-${end}`;
        },
        breadcrumbs() {
            return [
                { text: this.$t("farm.dashboard"), disabled: false, href: "#" },
                { text: this.$t("farm.farms"), disabled: false, href: "#" },
                { text: this.$t("farm.accessControl"), disabled: true, href: "#" },
            ];
        },
    },
    watch: {
        options: {
            handler(newVal, oldVal) {
                if (oldVal && (newVal.page !== oldVal.page || newVal.limit !== oldVal.limit)) {
                    this.loadFarmData();
                }
            },
            deep: true,
        },
        selectedUsers: {
            handler(newVal) {
                if (this.mode === 'view' && newVal && newVal.length > 0) {
                    this.loadUserAccessibleFarms().then(() => {
                        this.loadFarmData();
                    });
                }
            },
            immediate: false,
        },
    },
    data() {
        return {
            selectedFarms: [],
            debounceTimer: null,
            search: "",
            selectedSociety: null,
            societyList: [],
            loading: false,
            options: {
                limit: 10,
                page: 1,
                societyId: null,
            },
            headers: [],
            allSelectedHeaders: [],
            menu: false,
            farms: [],
            allFarms: [],
            totalFarms: 0,
            itemsPerPageOptions: [5, 10, 25, 50],
            itemsPerPageSlider: 0,
            selectedHeaders: [],
            tableHeaders: [],
            confirmGrantDialog: false,
            confirmGrant: {
                title: "",
                message: "",
                confirmBtnText: "",
                cancelBtnText: "",
                confirmData: {},
            },
            confirmRemoveDialog: false,
            confirmRemove: {
                title: "",
                message: "",
                confirmBtnText: "",
                cancelBtnText: "",
                confirmData: {},
            },
            userAccessibleFarmIds: [], // Farm IDs that the user has access to
            showActionsColumn: true,
            hideOnPrint: false,
        };
    },
    methods: {
    getSocietyList() {
      BuyingStationService.listBuyingStations({
        page: 1,
        limit: 1000,
        search: "",
        order: "ASC"
      }).then(res => {
        if (res.success && res.data) {
          const arryList = res.data.rows || res.data || [];
          this.societyList = arryList.map(item => ({
            name: item.fullName || `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim(),
            id: item.id,
          }));
        }
      }).catch(error => {
        console.error("Error fetching society list:", error);
      });
    },
    handleSocietyChange(society) {
      if (society && society.id) {
        this.options.societyId = society.id;
        this.options.page = 1; // Reset to first page when filter changes
      } else {
        this.options.societyId = null;
      }
      this.loadFarmData();
    },
    handleClearSociety() {
      this.selectedSociety = null;
      this.options.societyId = null;
      this.options.page = 1;
      this.loadFarmData();
    },
    async loadUserAccessibleFarms() {
            try {
                if (this.selectedUsers.length === 0) {
                    this.userAccessibleFarmIds = [];
                    return;
                }
                
                const userId = this.selectedUsers[0].id || this.selectedUsers[0].userId;
                
                // Try to fetch user's accessible farm IDs
                const params = {
                    societyId: this.options.societyId,
                    page: this.options.page,
                    limit: this.options.limit,
                    search: this.search,
                    order: "ASC"
                };
                try {
                    const response = await FarmService.getUserAccessibleFarms(userId, params);
                    if (response && response.data && response.data.response) {
                        if (response.data.response && Array.isArray(response.data.response)) {
                            this.userAccessibleFarmIds = response.data.response.map(f => f.id);
                        } 
                        else if (Array.isArray(response.data.farmIds)) {
                            this.userAccessibleFarmIds = response.data.farmIds;
                        } 
                        else if (Array.isArray(response.data)) {
                            this.userAccessibleFarmIds = response.data.map(f => f.farmId || f.id);
                        } 
                        else {
                            this.userAccessibleFarmIds = [];
                        }
                    } else {
                        this.userAccessibleFarmIds = [];
                    }
                } catch (apiError) {
                    console.warn('Could not fetch user accessible farms:', apiError);
                    this.userAccessibleFarmIds = [];
                }
            } catch (error) {
                console.error('Error loading user accessible farms:', error);
                this.userAccessibleFarmIds = [];
            }
        },
        async loadFarmData() {
            this.loading = true;
            try {
                const params = {
                    page: this.options.page,
                    limit: this.options.limit,
                    ...(this.search && { search: this.search }),
                    ...(this.options.societyId && { societyId: this.options.societyId }),
                };
                const response = await MemberDataService.getFarms(params);
                
                if (response.data && response.data.response && Array.isArray(response.data.response)) {
                    this.allFarms = response.data.response;
                    this.totalFarms = response.data.count || 0;
                } else if (response.data && Array.isArray(response.data)) {
                    this.allFarms = response.data;
                    this.totalFarms = response.total || response.data.length || 0;
                } else {
                    this.allFarms = [];
                    this.totalFarms = 0;
                } 
                
                this.allFarms = (this.allFarms || []).map(farm => {
                    let farmerName = '-';
                    if (farm.farmerName) {
                        farmerName = farm.farmerName;
                    } else if (farm.farmerFirstName && farm.farmerLastName) {
                        farmerName = `${farm.farmerFirstName} ${farm.farmerLastName}`.trim();
                    } else if (farm.user) {
                        const user = farm.user;
                        const nameParts = [user.firstName, user.middleName, user.lastName].filter(Boolean);
                        farmerName = nameParts.length > 0 ? nameParts.join(' ') : '-';
                    } else if (farm.farmer) {
                        farmerName = `${farm.farmer.firstName || ''} ${farm.farmer.lastName || ''}`.trim() || '-';
                    } else if (farm.ownerName) {
                        farmerName = farm.ownerName;
                    }

                    return {
                        id: farm.id,
                        farmId: farm.farmId || farm.registrationNo || farm.dimitraFarmId || farm.id,
                        farmName: farm.farmName || farm.name,
                        farmerName: farmerName,
                        farmAddress: farm.farmAddress || farm.address || '-',
                        regDate: farm.regDate || (farm.createdAt ? new Date(farm.createdAt).toLocaleDateString() : '-'),
                        societyId: farm.societyId || null,
                    };
                });
                
                // If in view mode, filter farms to show only those the user has access to
                if (this.mode === 'view' && this.selectedUsers.length > 0) {
                    await this.filterFarmsByUserAccess();
                } else {
                    this.farms = this.allFarms;
                }
                
            } catch (error) {
                console.error('Error loading farm data:', error);
                this.$notify({
                    title: "Error",
                    message: error?.data?.message || "Failed to load farms. Please try again.",
                    type: "error",
                });
                this.allFarms = [];
                this.farms = [];
                this.totalFarms = 0;
            } finally {
                this.loading = false;
            }
        },
        async filterFarmsByUserAccess() {
            // Filter farms to show only those the user has access to
            if (this.selectedUsers.length === 0) {
                this.farms = [];
                this.totalFarms = 0;
                return;
            }
            
            try {
                if (this.userAccessibleFarmIds.length === 0) {
                    await this.loadUserAccessibleFarms();
                }
                
                if (this.userAccessibleFarmIds.length > 0) {
                    this.farms = this.allFarms.filter(f => 
                        this.userAccessibleFarmIds.includes(f.id)
                    );
                    this.totalFarms = this.farms.length;
                    
                    await this.$nextTick();
                    this.selectedFarms = [...this.farms];
                } else {
                    this.farms = [];
                    this.totalFarms = 0;
                    this.selectedFarms = [];
                }
            } catch (error) {
                console.error('Error filtering farms by user access:', error);
                this.farms = this.allFarms;
                this.totalFarms = this.allFarms.length;
            }
        },
        searchQuery() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.options.page = 1;
                this.loadFarmData();
            }, 500);
        },
        pageChange(next) {
            if (next) this.options.page += 1;
            else this.options.page -= 1;
            this.loadFarmData();
        },
        async resetDefault() {
            this.tableHeaders = this.headers;
            this.selectedHeaders = this.allSelectedHeaders; 
            this.itemsPerPageSlider = 1;
            this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
            this.options.page = 1;
            this.menu = false;
            await this.loadFarmData();
        },
        async resetTableStructure() {
            this.tableHeaders = this.headers.filter((header) =>
                this.selectedHeaders.includes(header.text)
            );
            this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
            this.options.page = 1;
            this.menu = false;
            await this.loadFarmData();
        },
        selectFarm(item) {
            const index = this.selectedFarms.findIndex(f => f.id === item.id);
            if (index === -1) {
                this.selectedFarms.push(item);
            } else {
                this.selectedFarms.splice(index, 1);
            }
        },
        handleGrantAccess() {
            if (this.selectedFarms.length === 0) {
                return;
            }

            const userNames = this.selectedUsers.map(u => u.userName).join(', ');
            const farmCount = this.selectedFarms.length;

            this.confirmGrant.title = this.$t("farm.grantFarmAccess");
            this.confirmGrant.message = `${this.$t("farm.areYouSureYouWantToGrantAccessTo")} ${farmCount} ${this.$t("farm.farm")}${farmCount > 1 ? this.$t("farm.s") : ""} ${this.$t("farm.forTheFollowingUsers")}\n\n${userNames}`;
            this.confirmGrant.confirmBtnText = this.$t("farm.grantAccess");
            this.confirmGrant.confirmData = {
                users: this.selectedUsers,
                farms: this.selectedFarms,
            };
            this.confirmGrantDialog = true;
        },
        async confirmGrantAccess(confirmData) {
            this.confirmGrantDialog = false;
            this.startLoading();
            
            try {
                const payload = {
                    userIds: confirmData.users.map(u => u.id || u.userId),
                    farmIds: confirmData.farms.map(f => f.id || f.farmId),
                };
                
                await FarmService.grantFarmAccess(payload);
                
                this.$notify({
                    title: this.$t("farm.farmAccessGranted"),
                    message: `${this.$t("farm.successfullyGrantedAccessTo")} ${confirmData.farms.length} ${this.$t("farm.farm")}${confirmData.farms.length > 1 ? this.$t("farm.s") : ""} ${this.$t("farm.for")} ${confirmData.users.length} ${this.$t("farm.user")}${confirmData.users.length > 1 ? this.$t("farm.s") : ""}`,
                    type: "success",
                });
                
                if (this.mode === 'view') {
                    await this.loadFarmData();
                } else {
                    this.$emit('clear-selection');
                    this.$emit('back');
                }

                this.selectedFarms = [];
            } catch (error) {
                console.error('Error granting farm access:', error);
                this.$notify({
                    title: this.$t("farm.error"),
                    message: error?.data?.message || this.$t("farm.failedToGrantFarmAccessPleaseTryAgain"),
                    type: "error",
                });
            } finally {
                this.stopLoading();
            }
        },
        handleRemoveAccess() {
            if (this.selectedFarms.length === 0) {
                return;
            }

            const userNames = this.selectedUsers.map(u => u.userName).join(', ');
            const farmCount = this.selectedFarms.length;

            this.confirmRemove.title = this.$t("farm.removeFarmAccess");
            this.confirmRemove.message = `${this.$t("farm.areYouSureYouWantToRemoveAccessFrom")} ${farmCount} ${this.$t("farm.farm")}${farmCount > 1 ? this.$t("farm.s") : ""} ${this.$t("farm.forTheFollowingUsers")}\n\n${userNames}`;
            this.confirmRemove.confirmBtnText = this.$t("farm.removeAccess");
            this.confirmRemove.confirmData = {
                users: this.selectedUsers,
                farms: this.selectedFarms,
            };
            this.confirmRemoveDialog = true;
        },
        async confirmRemoveAccess(confirmData) {
            this.confirmRemoveDialog = false;
            this.startLoading();
            
            try {
                const payload = {
                    userIds: confirmData.users.map(u => u.id || u.userId),
                    farmIds: confirmData.farms.map(f => f.id || f.farmId),
                };
                
                await FarmService.removeUserAccess(payload);
                
                this.$notify({
                    title: this.$t("farm.farmAccessRemoved"),
                    message: `${this.$t("farm.successfullyRemovedAccessFrom")} ${confirmData.farms.length} ${this.$t("farm.farm")}${confirmData.farms.length > 1 ? this.$t("farm.s") : ""} ${this.$t("farm.for")} ${confirmData.users.length} ${this.$t("farm.user")}${confirmData.users.length > 1 ? this.$t("farm.s") : ""}`,
                    type: "success",
                });
                
                await this.loadUserAccessibleFarms();
                await this.loadFarmData();
                this.selectedFarms = [];
            } catch (error) {
                console.error('Error removing farm access:', error);
                this.$notify({
                    title: this.$t("farm.error"),
                    message: error?.data?.message || this.$t("farm.failedToRemoveFarmAccessPleaseTryAgain"),
                    type: "error",
                });
            } finally {
                this.stopLoading();
            }
        },
        async printPDF() {
            this.hideOnPrint = true;
            await this.$nextTick();
            
            const table = document.querySelector(".v-data-table__wrapper table");
            let removedHeader = null;
            let removedCells = [];
            
            // Remove the Access column (last column) before printing
            if (table) {
                const headerRow = table.querySelector("thead tr");
                if (headerRow && headerRow.children.length > 0) {
                    removedHeader = headerRow.removeChild(headerRow.lastElementChild);
                }
                const bodyRows = table.querySelectorAll("tbody tr");
                bodyRows.forEach((row) => {
                    if (row.children.length > 0) {
                        removedCells.push(row.removeChild(row.lastElementChild));
                    }
                });
            }
            
            await this.$nextTick();
            
            const element = document.querySelector(".v-data-table__wrapper");
            if (!element) {
                console.error("Table element not found");
                this.restoreTableColumns(table, removedHeader, removedCells);
                return;
            }
            
            let opt = {
                margin: 1,
                filename: "GrantFarmAccess.pdf",
                image: { type: "jpeg", quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
            };
            
            try {
                let worker = await html2pdf()
                    .set(opt)
                    .from(element)
                    .toCanvas()
                    .outputPdf("blob", "GrantFarmAccess.pdf");
                let file = new File([worker], "GrantFarmAccess.pdf", { type: "application/pdf" });
                var pdfUrl = await URL.createObjectURL(file);
                await printJS({ printable: `${pdfUrl}`, type: "pdf", showModal: true });
            } catch (error) {
                console.error("Error generating PDF:", error);
                this.$notify({
                    title: "Error",
                    message: "Failed to generate PDF. Please try again.",
                    type: "error",
                });
            } finally {
                // Restore the removed columns
                this.restoreTableColumns(table, removedHeader, removedCells);
                this.hideOnPrint = false;
                await this.$nextTick();
            }
        },
        restoreTableColumns(table, removedHeader, removedCells) {
            if (table && removedHeader) {
                const headerRow = table.querySelector("thead tr");
                if (headerRow) {
                    headerRow.appendChild(removedHeader);
                }
                const bodyRows = table.querySelectorAll("tbody tr");
                bodyRows.forEach((row, idx) => {
                    if (removedCells[idx]) {
                        row.appendChild(removedCells[idx]);
                    }
                });
            }
        },
    },
    mixins: [loadingMixin, download],
};
</script>

<style lang="scss" scoped>
.access-link {
    color: #1976d2 !important;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #1565c0 !important;
    }
}

.control {
    min-width: 120px;
    max-width: 220px;
    flex: 1 1 160px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

@media print {
    .action-col {
        display: none !important;
    }
}
</style>

